import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Box,
  Card,
  CardContent,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

const InvoiceSettings = () => {
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [termsAndCondition, setTermsAndCondition] = useState('');
  const [businessIdentity, setBusinessIdentity] = useState('Tax Id / Ein Number');
  const [invoiceLogo, setInvoiceLogo] = useState(null);

  useEffect(() => {
    const inv = settings.custom?.invoice;
    if (!inv) return;
    if (inv.termsAndCondition !== undefined) setTermsAndCondition(inv.termsAndCondition);
    if (inv.businessIdentity !== undefined) setBusinessIdentity(inv.businessIdentity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleTermsAndConditionChange = (event) => {
    setTermsAndCondition(event.target.value);
  };

  const handleBusinessIdentityChange = (event) => {
    setBusinessIdentity(event.target.value);
  };

  const handleInvoiceLogoChange = (event) => {
    if (event.target.files.length > 0) {
      setInvoiceLogo(event.target.files[0]);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      setInvoiceLogo(event.dataTransfer.files[0]);
    }
  };

  const handleBoxClick = () => {
    document.getElementById('fileUploadInput').click();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleSave = async () => {
    try {
      // Note: the invoice logo binary is not persisted through the settings API,
      // only the textual invoice settings are stored.
      await updateSettings({
        custom: {
          invoice: {
            termsAndCondition,
            businessIdentity,
            invoiceLogoName: invoiceLogo?.name || settings.custom?.invoice?.invoiceLogoName || '',
          },
        },
      }).unwrap();
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
      <CardContent>
        <Grid container spacing={2}>
          {/* Header */}
          <Grid item xs={12}>
            <Typography variant="h6" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main' }} /> Invoice Settings
            </Typography>
          </Grid>

          {/* Terms & Conditions */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Terms & Conditions"
              multiline
              rows={4}
              value={termsAndCondition}
              onChange={handleTermsAndConditionChange}
            />
          </Grid>

          {/* Invoice Logo Upload with Drag & Drop */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" color="text.primary">Invoice Logo (1000 X 300 Px)</Typography>
            <Box
              onClick={handleBoxClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                height: 150,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                textAlign: 'center',
              }}
            >
              <Box
                component="input"
                type="file"
                id="fileUploadInput"
                accept="image/*"
                onChange={handleInvoiceLogoChange}
                sx={{ display: 'none' }}
              />

              {invoiceLogo ? (
                <Box
                  component="img"
                  src={URL.createObjectURL(invoiceLogo)}
                  alt="Invoice Logo"
                  sx={{ maxWidth: '100%', maxHeight: 100 }}
                />
              ) : (
                <Typography color="text.secondary">Drag and drop file here or click to browse</Typography>
              )}
            </Box>
          </Grid>

          {/* Business Identity Selection */}
          <Grid item xs={12} md={6}>
            <Typography variant="body1" color="text.primary">Business Identity</Typography>
            <RadioGroup row value={businessIdentity} onChange={handleBusinessIdentityChange}>
              <FormControlLabel value="Tax Id / Ein Number" control={<Radio />} label="Tax Id / Ein Number" />
              <FormControlLabel value="Musak" control={<Radio />} label="Musak" />
            </RadioGroup>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Button variant="contained" color="primary" fullWidth onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </Grid>
        </Grid>

        <SaveSnackbar snack={snack} onClose={closeSnack} />
      </CardContent>
    </Card>
  );
};

export default InvoiceSettings;
